// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/ui/Button.tsx
import React from 'react';
import { Link } from 'react-router-dom';

type ButtonSize = 'S' | 'M' | 'L' | 'XL' | 'XXL';
type ButtonVariant = 'filled' | 'bordered' | 'plus_expand'; 

interface ButtonProps {
  children: React.ReactNode;
  size?: ButtonSize;
  variant?: ButtonVariant;
  to?: string;
  href?: string;
  onClick?: (e?: any) => void;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  isExpanded?: boolean;
  [key: string]: any;
}

const sizeConfigMap: Record<ButtonSize, { 
  text: string; 
  padding: string; 
  height: string; 
  collapsedWidth: string; 
  expandedWidth: string; 
  plusSize: string;
}> = {
  S: { 
    text: 'text-[13px]', 
    padding: 'px-7 py-1.5', 
    height: '32px', 
    collapsedWidth: '32px', 
    expandedWidth: '140px', 
    plusSize: 'text-[18px]' 
  },
  M: { 
    text: 'text-[15px]', 
    padding: 'px-9 py-2', 
    height: '38px', 
    collapsedWidth: '38px', 
    expandedWidth: '160px', 
    plusSize: 'text-[30px]' 
  },
  L: { 
    text: 'text-[17px]', 
    padding: 'px-11 py-2.5', 
    height: '44px', 
    collapsedWidth: '44px', 
    expandedWidth: '180px', 
    plusSize: 'text-[34px]' 
  },
  XL: { 
    text: 'text-xl', 
    padding: 'px-7 py-3', 
    height: '52px', 
    collapsedWidth: '52px', 
    expandedWidth: '200px', 
    plusSize: 'text-[38px]' 
  },
  XXL: { 
    text: 'text-2xl', 
    padding: 'px-8 py-4', 
    height: '64px', 
    collapsedWidth: '64px', 
    expandedWidth: '240px', 
    plusSize: 'text-[42px]' 
  },
};

const Button = ({
  children,
  size = 'XL',
  variant = 'filled',
  to,
  href,
  onClick,
  disabled = false,
  className = '',
  type = 'button',
  isExpanded = false,
  ...props
}: ButtonProps) => {

  const sizeConfig = sizeConfigMap[size] || sizeConfigMap.M;

  // Basis-Stile
  const baseStyles = `
    inline-flex items-center justify-center font-medium rounded-full
    whitespace-nowrap text-center
    ${variant !== 'plus_expand' ? sizeConfig.text : ''} 
    ${variant !== 'plus_expand' ? sizeConfig.padding : ''}
    ${disabled ? 'opacity-50 pointer-events-none' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  let variantStyles = '';
  let buttonContent: React.ReactNode = children;
  let customStyle: React.CSSProperties = { cursor: 'pointer' };

  if (variant === 'filled') {
    variantStyles = `
      bg-[#007add] text-white border border-[#007add]
      hover:opacity-80 transition-opacity duration-300
    `;
  } else if (variant === 'bordered') {
    variantStyles = `
      bg-transparent text-white border border-white
      hover:bg-white hover:text-black transition-colors duration-300
    `;
  } else if (variant === 'plus_expand') {
    // Originale Lösung: Animiere PADDING, nicht width!
    const circleSize = sizeConfig.collapsedWidth; // z.B. "44px"
    
    variantStyles = `
      bg-transparent text-white
      ${isExpanded ? 'border border-white' : 'border-0'}
      hover:bg-white hover:text-black overflow-hidden
      transition-all duration-700 ease-in-out
      ${isExpanded ? 'px-7' : 'px-3.5'}
    `;

    // Style für height (width wird durch padding gesteuert)
    customStyle = {
      cursor: 'pointer',
      height: sizeConfig.height,
      minWidth: circleSize, // Mindestbreite = Kreis
    };

    buttonContent = (
      <div className="relative flex items-center justify-center">
        {/* Plus-Zeichen - verschwindet beim Expand */}
        <span 
          className={`${sizeConfig.plusSize} leading-none transition-all duration-700 ease-in-out`}
          style={{
            position: isExpanded ? 'absolute' : 'relative',
            opacity: isExpanded ? 0 : 1,
            transform: isExpanded ? 'scale(0)' : 'scale(1)',
          }}
        >
          +
        </span>

        {/* Text "Mehr erfahren" - fade-in von rechts */}
        <span 
          className={`${sizeConfig.text} whitespace-nowrap transition-all duration-700 ease-in-out`}
          style={{
            position: isExpanded ? 'relative' : 'absolute',
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translateX(0)' : 'translateX(16px)',
          }}
        >
          {children || 'Mehr erfahren'}
        </span>
      </div>
    );
  }

  const finalStyles = `${baseStyles} ${variantStyles}`.trim().replace(/\s+/g, ' ');

  // --- RENDER LOGIK ---

  // SPECIAL: plus_expand im collapsed State ist immer ein button
  if (variant === 'plus_expand' && !isExpanded) {
    return (
      <button
        className={finalStyles}
        onClick={onClick}
        disabled={disabled}
        type="button"
        style={customStyle}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {buttonContent}
      </button>
    );
  }

  // Fall 1: 'href' (Externe Links)
  if (href) {
    return (
      <a
        href={href}
        className={finalStyles}
        onClick={(e) => { 
          if (onClick) onClick(e); 
        }}
        style={customStyle}
        {...props}
      >
        {buttonContent}
      </a>
    );
  }

  // Fall 2: 'to' (Interne Links)
  if (to) {
    return (
      <Link
        to={to}
        className={finalStyles}
        onClick={(e) => { 
          if (onClick) onClick(e); 
        }}
        style={customStyle}
        {...props}
      >
        {buttonContent}
      </Link>
    );
  }
  
  // Fall 3: 'onClick' (Standard-Button)
  return (
    <button
      className={finalStyles}
      onClick={onClick}
      disabled={disabled}
      type={type}
      style={customStyle}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {buttonContent}
    </button>
  );
};

export default Button;